import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@core/components/ui/dialog";
import { Button } from "@core/components/ui/button";
import { Checkbox } from "@core/components/ui/checkbox";
import { Input } from "@core/components/ui/input";
import { Label } from "@core/components/ui/label";
import { Textarea } from "@core/components/ui/textarea";
import { useToast } from "@core/hooks/use-toast";
import { browserTimeZone, DEFAULT_DURATION_MINUTES } from "@core/lib/add-to-calendar";
import {
  buildDecisionHint,
  buildFallbackAnnouncement,
  buildFinalCandidates,
  candidateToFinalSlot,
  formatFinalDate,
  pickDefaultCandidate,
  type FinalCandidate,
  type FinalDate,
} from "@core/lib/final-date";
import type { PollInsights, Slot } from "@core/lib/insights/types";
import {
  draftAnnouncement,
  notifySubscribers,
  setFinalDate,
  trackFinalDate,
} from "@core/services/FinalDateService";

const ANNOUNCEMENT_MAX = 600;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function addMinutes(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const total = (h * 60 + m + minutes) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

interface FixDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  poll: { id: string; title?: string | null };
  slots: Slot[];
  insights: PollInsights;
  /** Créneau à présélectionner (bandeau « créneau en tête », ou date déjà fixée). */
  initialSlotId?: string | null;
  /** « Clôturer sans fixer de date » : comportement historique. */
  onCloseWithoutDate?: () => void | Promise<void>;
  /** Appelé après enregistrement réussi. */
  onFixed?: (finalDate: FinalDate) => void;
}

export const FixDateDialog: React.FC<FixDateDialogProps> = ({
  open,
  onOpenChange,
  poll,
  slots,
  insights,
  initialSlotId,
  onCloseWithoutDate,
  onFixed,
}) => {
  const { t, i18n } = useTranslation("polls");
  const { toast } = useToast();
  const locale = i18n.language || "fr";
  const language = (locale.slice(0, 2) as "fr" | "en" | "es") || "fr";

  const candidates = useMemo(() => buildFinalCandidates(slots, insights), [slots, insights]);
  const hint = useMemo(() => buildDecisionHint(candidates), [candidates]);

  const [step, setStep] = useState<"choose" | "announce">("choose");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [allDay, setAllDay] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const draftRun = useRef(0);

  const selected: FinalCandidate | null =
    candidates.find((c) => c.slotId === selectedId) ?? null;

  // Réinitialise à chaque ouverture : présélection du créneau en tête.
  useEffect(() => {
    if (!open) return;
    const def = pickDefaultCandidate(candidates, initialSlotId ?? insights.winnerSlotId);
    setStep("choose");
    setSelectedId(def?.slotId ?? null);
    setAnnouncement("");
    setUsedFallback(false);
    setNotify(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Heures du candidat sélectionné (modifiables).
  useEffect(() => {
    if (!selected) return;
    setStart(selected.start ?? "");
    setEnd(selected.start ? (selected.end ?? addMinutes(selected.start, DEFAULT_DURATION_MINUTES)) : "");
    setAllDay(!selected.start);
  }, [selected?.slotId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isGroup = !!selected?.endDate;
  const timeValid = allDay || isGroup || (TIME_RE.test(start) && (!end || TIME_RE.test(end)));

  const finalDate = useMemo<FinalDate | null>(() => {
    if (!selected) return null;
    const fd = candidateToFinalSlot(selected, browserTimeZone());
    delete fd.start;
    delete fd.end;
    if (!allDay && !isGroup && TIME_RE.test(start)) {
      fd.start = start;
      if (TIME_RE.test(end) && end !== start) fd.end = end;
    }
    fd.yes = selected.yes;
    fd.maybe = selected.maybe;
    fd.total = selected.total;
    return fd;
  }, [selected, allDay, isGroup, start, end]);

  const candidateLabel = (c: FinalCandidate) => {
    const l = formatFinalDate(candidateToFinalSlot(c, "UTC"), locale);
    const day = c.label && c.endDate ? c.label : l.day;
    return l.time ? `${day} · ${l.time}` : day;
  };

  const shortLabel = (c: FinalCandidate) => {
    const l = formatFinalDate(candidateToFinalSlot(c, "UTC"), locale);
    return c.label && c.endDate ? c.label : l.time ? `${l.weekday} ${c.start}` : l.weekday;
  };

  const runDraft = async (fd: FinalDate) => {
    const run = ++draftRun.current;
    setDrafting(true);
    const fallback = buildFallbackAnnouncement(t, fd, poll.title || "", locale);
    const ai = await draftAnnouncement({ pollId: poll.id, finalDate: fd, language });
    if (run !== draftRun.current) return;
    setAnnouncement((ai ?? fallback).slice(0, ANNOUNCEMENT_MAX));
    setUsedFallback(!ai);
    setDrafting(false);
  };

  const goToAnnounce = () => {
    if (!finalDate || !timeValid) return;
    setStep("announce");
    void runDraft(finalDate);
  };

  const confirm = async () => {
    if (!finalDate) return;
    setSaving(true);
    try {
      const saved = await setFinalDate(poll.id, {
        ...finalDate,
        ...(announcement.trim() ? { announcement: announcement.trim().slice(0, ANNOUNCEMENT_MAX) } : {}),
      });
      toast({ title: t("final_date.fixed_toast_title"), description: t("final_date.fixed_toast_desc") });
      if (notify) {
        notifySubscribers(poll.id)
          .then((r) => {
            trackFinalDate("final_date_email_sent", poll.id, { sent: r.sent, failed: r.failed });
            // 200 avec des échecs (Resend refusé, lot interrompu) : la date
            // est fixée, mais l'organisateur doit savoir qui n'a rien reçu.
            const notReached = (r.failed ?? 0) + (r.remaining ?? 0);
            if (notReached > 0) {
              toast({
                title: t("final_date.notify_partial_failed", { count: notReached }),
                ...(r.sent > 0 ? { description: t("final_date.notified_toast", { count: r.sent }) } : {}),
                variant: "destructive",
              });
            } else if (r.sent > 0) {
              toast({ title: t("final_date.notified_toast", { count: r.sent }) });
            }
          })
          .catch(() => toast({ title: t("final_date.notify_failed"), variant: "destructive" }));
      }
      onFixed?.(saved);
      onOpenChange(false);
    } catch {
      toast({ title: t("final_date.fix_failed"), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const closeWithoutDate = async () => {
    trackFinalDate("final_date_closed_without_date", poll.id);
    onOpenChange(false);
    await onCloseWithoutDate?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent
        className="w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        data-testid="fix-date-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {step === "choose" ? t("final_date.dialog_title") : t("final_date.announce_title")}
          </DialogTitle>
          <DialogDescription>
            {step === "choose" ? t("final_date.dialog_desc") : t("final_date.announce_desc")}
          </DialogDescription>
        </DialogHeader>

        {step === "choose" ? (
          <div className="flex-1 min-h-0 flex flex-col gap-3">
            {hint.kind !== "none" && (
              <p className="rounded-lg bg-primary/10 px-3 py-2 text-sm text-foreground first-letter:uppercase" data-testid="fix-date-hint">
                {hint.kind === "single" &&
                  t("final_date.hint_single", { slot: shortLabel(hint.first), count: hint.first.yes })}
                {hint.kind === "lead" &&
                  t(hint.second.maybe > 0 ? "final_date.hint_lead_maybe" : "final_date.hint_lead", {
                    slot: shortLabel(hint.first),
                    count: hint.first.yes,
                    other: shortLabel(hint.second),
                    otherYes: hint.second.yes,
                    otherMaybe: hint.second.maybe,
                  })}
                {hint.kind === "tie" &&
                  t("final_date.hint_tie", {
                    slot: shortLabel(hint.first),
                    other: shortLabel(hint.second),
                    count: hint.first.yes,
                  })}
              </p>
            )}

            {candidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("final_date.no_slots")}</p>
            ) : (
              <div
                role="radiogroup"
                aria-label={t("final_date.dialog_title")}
                className="flex-1 min-h-0 max-h-[40vh] overflow-y-auto overscroll-contain rounded-lg border border-border divide-y divide-border"
                data-testid="fix-date-candidates"
              >
                {candidates.map((c, i) => {
                  const active = c.slotId === selectedId;
                  return (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={active}
                      key={c.slotId}
                      onClick={() => setSelectedId(c.slotId)}
                      data-testid={`fix-date-candidate-${c.slotId}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                        active ? "bg-primary/10" : "hover:bg-muted/60"
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`h-4 w-4 flex-shrink-0 rounded-full border-2 ${
                          active ? "border-primary bg-primary" : "border-muted-foreground/40"
                        }`}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-foreground truncate first-letter:uppercase">
                          {candidateLabel(c)}
                        </span>
                        <span className="block text-xs text-muted-foreground tabular-nums">
                          {t("final_date.candidate_counts", { yes: c.yes, maybe: c.maybe, count: c.total })}
                        </span>
                      </span>
                      {i === 0 && c.total > 0 && (
                        <span className="text-[10px] font-semibold uppercase text-success">
                          {t("final_date.leading_badge")}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {selected && !isGroup && (
              <div className="rounded-lg border border-border p-3 space-y-3" data-testid="fix-date-time">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={allDay}
                    onCheckedChange={(v) => {
                      const on = v === true;
                      setAllDay(on);
                      if (!on && !start) {
                        setStart("19:00");
                        setEnd(addMinutes("19:00", DEFAULT_DURATION_MINUTES));
                      }
                    }}
                    data-testid="fix-date-all-day"
                  />
                  {t("final_date.all_day_toggle")}
                </label>
                {!allDay && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="fix-date-start" className="text-xs">{t("final_date.start")}</Label>
                      <Input
                        id="fix-date-start"
                        type="time"
                        value={start}
                        onChange={(e) => {
                          const v = e.target.value;
                          // Garde la durée quand on décale le début.
                          if (TIME_RE.test(start) && TIME_RE.test(end) && TIME_RE.test(v)) {
                            const [sh, sm] = start.split(":").map(Number);
                            const [eh, em] = end.split(":").map(Number);
                            const dur = (eh * 60 + em - (sh * 60 + sm) + 1440) % 1440 || DEFAULT_DURATION_MINUTES;
                            setEnd(addMinutes(v, dur));
                          }
                          setStart(v);
                        }}
                        data-testid="fix-date-start"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="fix-date-end" className="text-xs">{t("final_date.end")}</Label>
                      <Input
                        id="fix-date-end"
                        type="time"
                        value={end}
                        onChange={(e) => setEnd(e.target.value)}
                        data-testid="fix-date-end"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-3">
            {finalDate && (
              <p className="text-sm font-medium text-foreground first-letter:uppercase">
                {selected ? candidateLabel({ ...selected, start: finalDate.start, end: finalDate.end }) : null}
              </p>
            )}
            <div className="relative">
              <Textarea
                value={announcement}
                onChange={(e) => setAnnouncement(e.target.value.slice(0, ANNOUNCEMENT_MAX))}
                rows={5}
                disabled={drafting}
                placeholder={drafting ? t("final_date.drafting") : ""}
                aria-label={t("final_date.announce_title")}
                data-testid="fix-date-announcement"
              />
              {drafting && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-md bg-background/70 text-sm text-muted-foreground">
                  <Sparkles className="h-4 w-4 animate-pulse" aria-hidden />
                  {t("final_date.drafting")}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                {usedFallback ? t("final_date.fallback_note") : t("final_date.ai_note")} ·{" "}
                {announcement.length}/{ANNOUNCEMENT_MAX}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={drafting || !finalDate}
                onClick={() => finalDate && runDraft(finalDate)}
                data-testid="fix-date-regenerate"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" aria-hidden />
                {t("final_date.regenerate")}
              </Button>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={notify}
                onCheckedChange={(v) => setNotify(v === true)}
                className="mt-0.5"
                data-testid="fix-date-notify"
              />
              <span>{t("final_date.notify_subscribers")}</span>
            </label>
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
          {step === "choose" ? (
            <>
              {onCloseWithoutDate ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeWithoutDate}
                  className="text-muted-foreground"
                  data-testid="fix-date-close-without"
                >
                  {t("final_date.close_without_date")}
                </Button>
              ) : (
                <span />
              )}
              <Button
                type="button"
                onClick={goToAnnounce}
                disabled={!selected || !timeValid}
                data-testid="fix-date-continue"
              >
                {t("final_date.continue")}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => setStep("choose")} disabled={saving}>
                {t("final_date.back")}
              </Button>
              <Button
                type="button"
                onClick={confirm}
                disabled={saving || drafting || !finalDate}
                data-testid="fix-date-confirm"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />}
                {t("final_date.confirm")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FixDateDialog;
